// src/utils/UtilsOrders/getDocumentAndHistory.jsx
import {
  collection,
  doc,
  getDocFromCache,
  getDocFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";

// Utilidad: normaliza Timestamp/Date/number -> number (ms)
const toMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === "number") return ts;
  return 0;
};

// ──────────────────────────────────────────────────────────────────────────────
// API PÚBLICA
// ──────────────────────────────────────────────────────────────────────────────

export const getDocumentAndHistory = async (
  id,
  prefixes,
  collections,
  options = {}
) => {
  const {
    historyLimit = 20,
    companyIdHint = "",
    companiesData = [],
    altCollection = process.env.REACT_APP_COL_FALABELLA_ORDERS,
    mainHistorySub = "statuses",
    altHistorySub = "status",
    falabellaPrefix = "F",
  } = options;

  // Validaciones defensivas
  if (!id || typeof id !== "string") {
    console.error("getDocumentAndHistory: id inválido:", id);
    return { foundDocumentData: null, documentHistory: [], prefix: "" };
  }
  if (!Array.isArray(prefixes) || prefixes.length === 0) {
    console.error("getDocumentAndHistory: prefixes vacío/invalid:", prefixes);
    return { foundDocumentData: null, documentHistory: [], prefix: "" };
  }
  if (!Array.isArray(collections) || collections.length === 0) {
    console.error(
      "getDocumentAndHistory: collections vacío/invalid:",
      collections
    );
    return { foundDocumentData: null, documentHistory: [], prefix: "" };
  }
  const safeHistoryLimit = Math.max(1, Number(historyLimit) || 20);

  const candidates = buildCandidates(id, prefixes, collections, {
    companyIdHint,
    companiesData,
  });

  // Resuelve primero cache, luego server
  const resolved =
    (await tryResolveFirstExisting(candidates)) ||
    (await tryResolveFirstExisting(candidates, { forceServer: true }));

  if (!resolved) {
    console.warn("getDocumentAndHistory: no se encontró el documento para", {
      id,
      candidates,
    });
    return { foundDocumentData: null, documentHistory: [], prefix: "" };
  }

  const { collectionName, docId, prefix } = resolved;

  // Historial principal
  const history = await fetchHistory(collectionName, docId, {
    sub: mainHistorySub,
    historyLimit: safeHistoryLimit,
  });

  let mergedDoc = resolved.data || null;
  let mergedHistory = history;

  // Merge Falabella si corresponde
  if (
    prefix === falabellaPrefix &&
    typeof altCollection === "string" &&
    altCollection
  ) {
    const docIdAlt = docId.replace(/^F/, "");
    const altDoc = await fetchDocCacheFirst(altCollection, docIdAlt);
    if (altDoc) mergedDoc = { ...altDoc, ...mergedDoc };

    const altHist = await fetchHistory(altCollection, docIdAlt, {
      sub: altHistorySub,
      historyLimit: safeHistoryLimit,
    });
    mergedHistory = mergeHistories(history, altHist);
  }

  return {
    foundDocumentData: mergedDoc,
    documentHistory: mergedHistory,
    prefix,
  };
};

/**
 * Suscripción viva doc+historial (con errores claros).
 * @returns {Promise<() => void>} - unsubscribe de todos los listeners
 */
export const subscribeDocumentAndHistory = async (
  id,
  prefixes,
  collections,
  onData,
  options = {}
) => {
  const {
    historyLimit = 20,
    companyIdHint = "",
    companiesData = [],
    altCollection = process.env.REACT_APP_COL_FALABELLA_ORDERS,
    mainHistorySub = "statuses",
    altHistorySub = "status",
    falabellaPrefix = "F",
  } = options;

  if (!id || typeof id !== "string") {
    console.error("subscribeDocumentAndHistory: id inválido:", id);
    return () => {};
  }
  if (!Array.isArray(prefixes) || prefixes.length === 0) {
    console.error(
      "subscribeDocumentAndHistory: prefixes vacío/invalid:",
      prefixes
    );
    return () => {};
  }
  if (!Array.isArray(collections) || collections.length === 0) {
    console.error(
      "subscribeDocumentAndHistory: collections vacío/invalid:",
      collections
    );
    return () => {};
  }
  if (typeof onData !== "function") {
    console.error("subscribeDocumentAndHistory: onData debe ser función");
    return () => {};
  }
  const safeHistoryLimit = Math.max(1, Number(historyLimit) || 20);

  const candidates = buildCandidates(id, prefixes, collections, {
    companyIdHint,
    companiesData,
  });

  const resolved =
    (await tryResolveFirstExisting(candidates)) ||
    (await tryResolveFirstExisting(candidates, { forceServer: true }));

  if (!resolved) {
    console.warn("subscribeDocumentAndHistory: documento no encontrado", {
      id,
      candidates,
    });
    // Emite vacío para que la UI pueda reaccionar
    onData({ doc: null, history: [], prefix: "" });
    return () => {};
  }

  const { collectionName, docId, prefix } = resolved;

  let lastMainDoc = resolved.data || null;
  let lastAltDoc = null;
  let lastMainHist = [];
  let lastAltHist = [];

  const emit = () => {
    const docMerged =
      prefix === falabellaPrefix &&
      typeof altCollection === "string" &&
      altCollection
        ? { ...(lastAltDoc || {}), ...(lastMainDoc || {}) }
        : lastMainDoc;

    const histMerged =
      prefix === falabellaPrefix &&
      typeof altCollection === "string" &&
      altCollection
        ? mergeHistories(lastMainHist, lastAltHist)
        : lastMainHist;

    onData({
      doc: docMerged || null,
      history: histMerged,
      prefix,
    });
  };

  const unsubs = [];

  // --- main doc ---
  try {
    const mainDocRef = doc(db, collectionName, docId);
    unsubs.push(
      onSnapshot(
        mainDocRef,
        (snap) => {
          lastMainDoc = snap.exists() ? snap.data() : null;
          emit();
        },
        (err) => {
          console.error(
            "onSnapshot mainDoc ERROR:",
            err.code,
            err.message,
            err
          );
          emit(); // emite último estado conocido
        }
      )
    );
  } catch (e) {
    console.error("subscribeDocumentAndHistory mainDocRef error:", e);
  }

  // --- main history ---
  try {
    const mainHistRef = query(
      collection(db, collectionName, docId, mainHistorySub),
      orderBy("createdAt", "desc"),
      limit(safeHistoryLimit)
    );
    unsubs.push(
      onSnapshot(
        mainHistRef,
        (qs) => {
          lastMainHist = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
          emit();
        },
        (err) => {
          console.error(
            "onSnapshot mainHistory ERROR:",
            err.code,
            err.message,
            err
          );
        }
      )
    );
  } catch (e) {
    console.error("subscribeDocumentAndHistory mainHistory error:", e);
  }

  // --- alternativo (Falabella) ---
  if (
    prefix === falabellaPrefix &&
    typeof altCollection === "string" &&
    altCollection
  ) {
    const docIdAlt = docId.replace(/^F/, "");

    try {
      const altDocRef = doc(db, altCollection, docIdAlt);
      unsubs.push(
        onSnapshot(
          altDocRef,
          (snap) => {
            lastAltDoc = snap.exists() ? snap.data() : null;
            emit();
          },
          (err) => {
            console.error(
              "onSnapshot altDoc ERROR:",
              err.code,
              err.message,
              err
            );
          }
        )
      );
    } catch (e) {
      console.error("subscribeDocumentAndHistory altDocRef error:", e);
    }

    try {
      const altHistRef = query(
        collection(db, altCollection, docIdAlt, altHistorySub),
        orderBy("createdAt", "desc"),
        limit(safeHistoryLimit)
      );
      unsubs.push(
        onSnapshot(
          altHistRef,
          (qs) => {
            lastAltHist = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
            emit();
          },
          (err) => {
            console.error(
              "onSnapshot altHistory ERROR:",
              err.code,
              err.message,
              err
            );
          }
        )
      );
    } catch (e) {
      console.error("subscribeDocumentAndHistory altHistory error:", e);
    }
  }

  // Primera emisión con lo que ya tenemos
  emit();

  return () => {
    unsubs.forEach((u) => {
      try {
        u && u();
      } catch {}
    });
  };
};

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const fetchDocCacheFirst = async (collectionName, id) => {
  try {
    const ref = doc(db, collectionName, id);
    // 1) cache
    try {
      const snapCache = await getDocFromCache(ref);
      if (snapCache?.exists()) return snapCache.data();
    } catch {
      /* cache miss ok */
    }
    // 2) server
    const snapServer = await getDocFromServer(ref);
    return snapServer.exists() ? snapServer.data() : null;
  } catch (e) {
    console.error(`fetchDocCacheFirst error ${collectionName}/${id}:`, e);
    return null;
  }
};

const fetchDocFrom = async (collectionName, id, source) => {
  try {
    const ref = doc(db, collectionName, id);
    if (source === "cache") {
      const snap = await getDocFromCache(ref);
      return snap.exists() ? snap.data() : null;
    }
    if (source === "server") {
      const snap = await getDocFromServer(ref);
      return snap.exists() ? snap.data() : null;
    }
    console.error("fetchDocFrom: source inválido:", source);
    return null;
  } catch (e) {
    console.error(`fetchDocFrom error ${collectionName}/${id}/${source}:`, e);
    return null;
  }
};

const tryResolveFirstExisting = async (
  candidates,
  { forceServer = false } = {}
) => {
  const results = await Promise.allSettled(
    candidates.map(async (c) => {
      const data = forceServer
        ? await fetchDocFrom(c.collectionName, c.docId, "server")
        : await fetchDocCacheFirst(c.collectionName, c.docId);
      return data ? { ...c, data } : null;
    })
  );

  for (let i = 0; i < results.length; i++) {
    const r = results[i];
    if (r.status === "fulfilled" && r.value) return r.value;
  }
  return null;
};

const buildCandidates = (
  id,
  prefixes,
  collections,
  { companyIdHint, companiesData }
) => {
  const list = [];
  const trimmed = (id || "").trim();

  // Caso 1: id ya trae prefijo
  const matchedPrefix = prefixes.find((p) => trimmed.startsWith(p));
  if (matchedPrefix) {
    for (const col of collections) {
      if (typeof col !== "string" || !col) {
        console.error("buildCandidates: nombre de colección inválido:", col);
        continue;
      }
      list.push({
        collectionName: col,
        docId: trimmed,
        prefix: matchedPrefix,
      });
    }
    return list;
  }

  // Caso 2/3: sin prefijo → prioriza por companyIdHint
  const hintedPrefix =
    (companyIdHint &&
      companiesData?.find?.((c) => c.companyID === companyIdHint)?.prefix) ||
    "";

  const orderedPrefixes = hintedPrefix
    ? [hintedPrefix, ...prefixes.filter((p) => p !== hintedPrefix)]
    : [...prefixes];

  for (const col of collections) {
    if (typeof col !== "string" || !col) {
      console.error("buildCandidates: nombre de colección inválido:", col);
      continue;
    }
    for (const pref of orderedPrefixes) {
      list.push({
        collectionName: col,
        docId: `${pref}${trimmed}`,
        prefix: pref,
      });
    }
  }
  return list;
};

const fetchHistory = async (
  collectionName,
  docId,
  { sub = "statuses", historyLimit = 20 } = {}
) => {
  try {
    const safeLimit = Math.max(1, Number(historyLimit) || 20);
    const q = query(
      collection(db, collectionName, docId, sub),
      orderBy("createdAt", "desc"),
      limit(safeLimit)
    );
    const qs = await getDocs(q);
    return qs.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (toMs(a?.createdAt) < toMs(b?.createdAt) ? 1 : -1));
  } catch (e) {
    console.error(`fetchHistory error ${collectionName}/${docId}/${sub}:`, e);
    return [];
  }
};

const mergeHistories = (h1 = [], h2 = []) =>
  [...h1, ...h2].sort((a, b) =>
    toMs(a?.createdAt) < toMs(b?.createdAt) ? 1 : -1
  );
